/**
 * Hooks React Query du domaine chantier.
 *
 * L'ancien affichage confondait avancement des travaux et budget consommé.
 * `ProjectSummary.health` sépare désormais les deux — voir `computeProjectHealth`
 * dans `@dker/core`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DomainError } from "@dker/core";
import type {
  ChangeProjectStatusInput,
  ExecutionContext,
  ProjectDetail,
  ProjectSummary,
  SaveLotInput,
  SavePhaseInput,
  SaveProjectInput,
  SetPhasePredecessorsInput,
  ShiftPhasesInput,
} from "@dker/core";
import { projectServices, serviceDeps, useExecutionContext } from "@/services";
import { useTenantOptional } from "@/hooks/useTenant";
import { useUiStore } from "@/stores/uiStore";

export type { ProjectDetail, ProjectSummary };

function toMessage(error: unknown): string {
  if (error instanceof DomainError) return error.message;
  return "Une erreur inattendue est survenue. Réessayez dans un instant.";
}

export function useProjects() {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["projects", tenant?.id],
    enabled: !!tenant,
    queryFn: () => projectServices.listerChantiers(makeContext(), {}, serviceDeps),
  });
}

export function useActiveProjects() {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["projects", tenant?.id, "active"],
    enabled: !!tenant,
    queryFn: () => projectServices.listerChantiersActifs(makeContext(), {}, serviceDeps),
  });
}

/** Chantiers déficitaires ou qui consomment leur budget trop vite. */
export function useDriftingProjects() {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["projects", tenant?.id, "drifting"],
    enabled: !!tenant,
    queryFn: () => projectServices.chantiersEnDerive(makeContext(), {}, serviceDeps),
  });
}

export function useProject(projectId: string | undefined) {
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["project", projectId],
    enabled: !!projectId,
    queryFn: () =>
      projectServices.lireChantier(makeContext(), { projectId: projectId! }, serviceDeps),
  });
}

export function useSaveProject() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: SaveProjectInput) =>
      projectServices.enregistrerChantier(makeContext(), input, serviceDeps),

    onSuccess: (project, input) => {
      void qc.invalidateQueries({ queryKey: ["projects", tenant?.id] });
      void qc.invalidateQueries({ queryKey: ["project", project.id] });
      addToast({
        type: "success",
        title: input.projectId ? "Chantier modifié" : "Chantier créé",
      });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message: toMessage(error),
      }),
  });
}

/**
 * Changer le statut d'un chantier.
 *
 * Tout est rafraîchi, pas seulement la fiche : le statut décide du tableau de
 * bord, du planning, des rappels de sécurité et de la file du soir. Un geste
 * rare, qui mérite que les autres écrans disent tout de suite la vérité.
 */
export function useChangeProjectStatus() {
  const qc = useQueryClient();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: ChangeProjectStatusInput) =>
      projectServices.changerStatutChantier(makeContext(), input, serviceDeps),
    onSuccess: () => {
      void qc.invalidateQueries();
      addToast({ type: "success", title: "Statut du chantier modifié" });
    },
    onError: (error) =>
      addToast({ type: "error", title: "Statut non modifié", message: toMessage(error) }),
  });
}

/* ─── Les phases ────────────────────────────────────────────────────────── */

/**
 * Les clés à rafraîchir après avoir touché une phase.
 *
 * L'avancement d'une phase remonte plus loin qu'on ne croit : la fiche
 * chantier, la colonne « Avanc. » du tableau de bord, et surtout la file du
 * soir — dont le signal « établir une situation » compare l'avancement à la
 * part facturée. Une phase avancée sans rafraîchir la file laisserait le
 * patron sans la proposition qu'elle vient de rendre vraie.
 */
export function clesAvancement(projectId: string | undefined, tenantId: string | undefined) {
  return [
    ["project", projectId],
    ["projects", tenantId],
    ["pilotage-rentabilite", tenantId],
    ["file-du-soir", tenantId],
    ["missions", tenantId],
  ];
}

export function useSavePhase(projectId?: string) {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: SavePhaseInput) =>
      projectServices.enregistrerPhase(makeContext(), input, serviceDeps),
    onSuccess: (_phase, input) => {
      for (const key of clesAvancement(projectId ?? input.projectId, tenant?.id)) {
        void qc.invalidateQueries({ queryKey: key });
      }
      addToast({
        type: "success",
        title: input.phaseId ? "Phase mise à jour" : "Phase ajoutée",
      });
    },
    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

/**
 * Poser d'un coup les phases types du métier.
 *
 * Huit intitulés tapés un par un sur un téléphone, c'est ce qui fait qu'un
 * chantier reste sans aucune phase — et sans phase, son avancement est inconnu
 * et le tableau de bord ne propose aucune situation à facturer. Une seule
 * notification à la fin, et un seul rafraîchissement : huit notifications pour
 * un seul geste seraient huit fois le même message.
 */
export function useSeedPhases(projectId: string) {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: async ({ noms, depuis }: { noms: readonly string[]; depuis: number }) => {
      // En série et non en parallèle : chaque phase porte son rang, et le
      // service les écrit dans l'ordre où il les reçoit.
      for (const [i, name] of noms.entries()) {
        await projectServices.enregistrerPhase(
          makeContext(),
          {
            projectId,
            name,
            sortOrder: depuis + i,
            progressPercent: 0,
            status: "pending",
            // Sans dates : ce sont des intitulés proposés, pas un calendrier.
            // Les poser au hasard ferait entrer de fausses échéances dans la
            // surveillance des chantiers.
            plannedStart: null,
            plannedEnd: null,
          },
          serviceDeps,
        );
      }
      return noms.length;
    },
    onSuccess: (posees) => {
      for (const key of clesAvancement(projectId, tenant?.id)) {
        void qc.invalidateQueries({ queryKey: key });
      }
      addToast({
        type: "success",
        title: `${posees} phase${posees > 1 ? "s" : ""} ajoutée${posees > 1 ? "s" : ""}`,
        message: "À renommer ou à retirer librement : ce sont des propositions.",
      });
    },
    onError: (error) =>
      addToast({
        type: "error",
        title: "Phases non ajoutées",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

export function useRemovePhase(projectId?: string) {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (phaseId: string) =>
      projectServices.supprimerPhase(makeContext(), { phaseId }, serviceDeps),
    onSuccess: () => {
      for (const key of clesAvancement(projectId, tenant?.id)) {
        void qc.invalidateQueries({ queryKey: key });
      }
      // Les déclarations qui s'y rattachaient sont parties avec elle —
      // `field_reports.phase_id` est en ON DELETE CASCADE. La boîte de
      // réception du terrain doit donc se relire aussi.
      void qc.invalidateQueries({ queryKey: ["field-reports", tenant?.id] });
      addToast({ type: "success", title: "Phase supprimée" });
    },
    onError: (error) =>
      addToast({
        type: "error",
        title: "Suppression impossible",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

/* ─── Les lots et les enchaînements ─────────────────────────────────────── */

/**
 * Une mutation de planning : mêmes clés que l'avancement — un lot supprimé
 * emporte des phases, un décalage change les échéances que surveille le
 * tableau de bord — et la même façon de dire un refus.
 */
function usePlanningMutation<I, R>(
  projectId: string,
  run: (ctx: ExecutionContext, input: I) => Promise<R>,
  messages: { ok: (input: I, result: R) => string; ko: string },
) {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: I) => run(makeContext(), input),
    onSuccess: (result, input) => {
      for (const key of clesAvancement(projectId, tenant?.id)) {
        void qc.invalidateQueries({ queryKey: key });
      }
      addToast({ type: "success", title: messages.ok(input, result) });
    },
    onError: (error) => addToast({ type: "error", title: messages.ko, message: toMessage(error) }),
  });
}

export function useSaveLot(projectId: string) {
  return usePlanningMutation(
    projectId,
    (ctx, input: SaveLotInput) => projectServices.enregistrerLot(ctx, input, serviceDeps),
    {
      ok: (input) =>
        input.lotId
          ? "Lot mis à jour"
          : input.phases.length > 0
            ? `Lot ajouté, avec ${input.phases.length} phase${input.phases.length > 1 ? "s" : ""}`
            : "Lot ajouté",
      ko: "Lot non enregistré",
    },
  );
}

export function useRemoveLot(projectId: string) {
  return usePlanningMutation(
    projectId,
    (ctx, lotId: string) => projectServices.supprimerLot(ctx, { lotId }, serviceDeps),
    { ok: () => "Lot supprimé, avec ses phases", ko: "Suppression impossible" },
  );
}

export function useSetPredecessors(projectId: string) {
  return usePlanningMutation(
    projectId,
    (ctx, input: SetPhasePredecessorsInput) => projectServices.enchainerPhase(ctx, input, serviceDeps),
    { ok: () => "Enchaînements enregistrés", ko: "Enchaînements non enregistrés" },
  );
}

export function useShiftPhases(projectId: string) {
  return usePlanningMutation(
    projectId,
    (ctx, input: ShiftPhasesInput) => projectServices.decalerPhases(ctx, input, serviceDeps),
    {
      ok: (_input, n) => `${n} phase${n > 1 ? "s" : ""} décalée${n > 1 ? "s" : ""}`,
      ko: "Décalage non appliqué",
    },
  );
}
